import React, {
  useEffect,
  useState,
  useContext,
  useImperativeHandle
} from 'react';
import {
  getTranslatedText,
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator,
  EventEmitter,
  DeleteModal,
  segmentTimerConstraints,
  TEXT_LENGTH,
  sendMessageToContentJS,
  AccessControl,
  COMMON_CONSTANTS,
  EventEmitterWithIndex,
  useCheckFeatureGating,
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP
} from '@nile/components';
import {
  Dropdown,
  Avatar,
  ButtonGroup,
  Button,
  TextField,
  TextStyle,
  Link
} from '@superops/ui-design-system';
import { IconHolder } from './utils';
import { validate } from 'validate.js';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import { isEmpty, isEqual, pick } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Tooltip } from 'react-clone-tippy';
import { useStopwatch } from 'react-timer-hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BlockItemConsumptionInfo } from './BlockItemConsumptionInfo';
import AssociateWorkItem from './AssociateWorkItem';
import WorklogTask from './WorklogTask';
import { AIOnboardingRatingComponent } from '@nile/components';

const DataGetter = styled.div`
  .value_selector__control {
    background: none !important;
    border-bottom: none !important;
  }
  .Polaris-Connected,
  .Superops-Connected {
    padding-top: 0 !important;
  }
  .Polaris-TextField,
  .Superops-TextField {
    background: transparent;
  }
  .Polaris-TextField__Input,
  .Superops-TextField__Input {
    padding-top: 5px;
    padding-bottom: 0;
    border-bottom: 0 !important;
    min-height: 70px;
    max-height: 200px;
    overflow: auto;
  }
  .Polaris-TextField--multiline > .Polaris-TextField__Input,
  .Superops-TextField--multiline > .Superops-TextField__Input {
    position: relative;
    // top: -10px;
  }
`;

const TimerCell = styled.div`
  min-height: 38px;
  transition: background 200ms linear;
`;

const Timer = styled.div`
  .blink {
    animation: blink 1s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite;
  }
`;

const Wrapper = styled.div`
  border-radius: 0 0px 10px 10px;
  transition: all 200ms linear;
`;

let TIMER_ID = null;

function WorklogAutoTimerForm(props) {
  const {
    intl,
    editMode,
    options,
    selectedItem,
    itemsForTimeTrackingLoading,
    currentUser,
    timerItem,
    defaultTimerItem,
    comments,
    timespent,
    billable,
    timerState,
    running,
    handlePropChange,
    createWorklogTimerEntry,
    createWorklogTimerEntryLoading,
    updateWorklogTimerEntry,
    updateWorklogTimerEntryLoading,
    createWorklogEntryFromTimer,
    createWorklogEntryFromTimerLoading,
    deleteWorklogTimerEntry,
    deleteWorklogTimerEntryLoading,
    module = null,
    ticket,
    project,
    workItem,
    timerId,
    timer,
    segments,
    associateWorkItem,
    associatedWorkItem,
    reAssociateWorkItem,
    handleAssociateWorkItem,
    handleClose,
    forwardedRef,
    hideCancel,
    hideWorkitem,
    hideDelete,
    handleParentClose,
    aiGeneratedNote,
    setAIgeneratedNote,
    timerNote,
    isAgentChat = false,
    autoFocusTimerNotes,
    taskId,
    hideWorklogTask,
    hideTaskWorkItem,
    taskViewOnly,
    hideBlockItemConsumptionInfo
  } = props;

  const homeContext = useContext(HomeContext);

  const [showAiNote, setShowAiNote] = useState(isAgentChat);

  const iconMap = {
    RUNNING: 'Pause'
  };

  let stopwatchOffset = new Date();
  stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + (timespent || 0));
  const {
    seconds,
    minutes,
    hours,
    days,
    isRunning,
    start,
    pause,
    reset
  } = useStopwatch({
    autoStart: running,
    offsetTimestamp: stopwatchOffset
  });
  const flags = useFlags();
  const [errors, setErrors] = useState(null);

  const preventBlurRef = React.useRef(null);

  const associatedTaskToTimer = !isEmpty(timer?.task) ? timer?.task : null;

  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  useEffect(() => {
    stopwatchOffset = new Date();
    stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + (timespent || 0));
    reset(stopwatchOffset, running);
  }, [running, timespent]);

  const handlePropertyChange = (property, value) => {
    // if (!timerId || editMode) {
    if (!timerId) {
      handlePropChange(property, value);
    } else {
      let inputVal = value;
      if (TIMER_ID) {
        clearTimeout(TIMER_ID);
      }
      if (property === 'serviceItem') {
        inputVal = { itemId: value.itemId };
      }
      if (property === 'serviceCatalogItem') {
        inputVal = { itemId: value.itemId };
      }
      updateWorklogTimerEntry({
        variables: {
          timerEntryInput: {
            timerId,
            [property]: inputVal,
            notes: comments
          }
        }
      }).then((res) => {
        const ts = res?.data?.updateWorklogTimerEntry?.timespent;
        const stopwatchOffset = new Date();
        stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + ts);
        reset(stopwatchOffset, res?.data?.updateWorklogTimerEntry?.running);
      });
    }
  };

  const handleBillable = () => {
    handlePropertyChange('billable', !billable);
  };

  const validateTimer = (allowEmptyNotes) => {
    const validationContraints = {
      notes: {
        type: 'string',
        presence: {
          allowEmpty: false
        }
      },
      ...segmentTimerConstraints
    };

    if (allowEmptyNotes) {
      delete validationContraints.notes;
    }
    const result = validate(
      { notes: comments, segments: segments },
      validationContraints
    );

    return result;
  };

  const handleTimerClick = (runningState) => {
    const validationResult = validateTimer(
      props?.allowEmptyNotes ||
        (workItem?.module === 'TICKET' && workItem?.workId)
    );
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setErrors(null);

    let inputObj = {
      billable,
      notes: comments,
      type: 'AUTOMATIC',
      workItem: workItem?.workId
        ? {
            workId: workItem?.workId,
            module: workItem?.module || 'TICKET'
          }
        : null
    };
    if (selectedItem) {
      inputObj = {
        ...inputObj,
        [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
          itemId: selectedItem?.itemId
        }
      };
    }

    if (createWorklogTimerEntryLoading || updateWorklogTimerEntryLoading)
      return;
    if (!timerId) {
      if (taskId) {
        inputObj.task = {
          taskId: taskId
        };
      }

      createWorklogTimerEntry({
        variables: {
          timerEntryInput: inputObj
        }
      }).then((res) => {
        // reset(timespent);
        // start();
      });
    } else {
      // if (TIMER_ID) {
      //   clearTimeout(TIMER_ID);
      // }
      updateWorklogTimerEntry({
        variables: {
          timerEntryInput: {
            ...inputObj,
            timerId,
            running: typeof runningState === 'boolean' ? runningState : running
          }
        }
      }).then((res) => {
        // const ts = res?.data?.updateWorklogTimerEntry?.timespent;
        // const stopwatchOfset = new Date();
        // stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + ts);
        // stopwatchOfset.setSeconds(stopwatchOfset.getSeconds());
        // reset(stopwatchOfset, res?.data?.updateWorklogTimerEntry?.running);
        EventEmitterWithIndex.dispatch('refetchGlobalTimers');
        sendMessageToContentJS('refetchGlobalTimers');
      });
    }
  };
  const handleAddWorklog = () => {
    const validationResult = validateTimer();
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setErrors(null);

    if (timerId) {
      // clearTimeout(TIMER_ID);
      updateWorklogTimerEntry({
        variables: {
          timerEntryInput: {
            type: 'AUTOMATIC',
            timerId,
            notes: comments,
            billable,
            [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: selectedItem
              ? {
                  itemId: selectedItem?.itemId
                }
              : null,
            workItem: workItem
              ? {
                  workId: workItem?.workId,
                  module: workItem?.module
                }
              : null
          }
        }
      }).then((res) => {
        const ts = res?.data?.updateWorklogTimerEntry?.timespent;
        const stopwatchOffset = new Date();
        stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + ts);
        reset(stopwatchOffset);
        createWorklogEntryFromTimer({ timer: timer });
      });
    } else {
      createWorklogEntryFromTimer({ timer: timer });
    }
  };

  const resetWorklog = () => {
    deleteWorklogTimerEntry({
      variables: {
        deleteWorklogTimerInput: {
          timerId
        }
      }
    }).then((res) => {
      reset();
      pause();
    });
  };

  const formatTime = (time) => {
    return String(time).padStart(2, '0');
  };

  const handleNewTicketCreation = (timerData, workItemData) => {
    if (isEmpty(timerData?.serviceCatalogItem)) {
      homeContext.closeCurrentTab();
      window.setTimeout(() => {
        homeContext.openTab({
          module: 'TICKET',
          data: {
            subject: workItemData?.subject,
            displayId: workItemData?.displayId,
            ticketId: workItemData?.ticketId
          },
          ticketId: workItemData?.ticketId
        });
      }, 500);
    }
  };

  const handleCreateWorkLogFromCreateTicket = (workItemData) => {
    const validationResult = validateTimer();
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setErrors(null);

    const workItemObj = workItemData || workItem;

    let inputObj = {
      billable,
      notes: comments,
      type: 'AUTOMATIC',
      workItem:
        workItemObj?.workId || workItemObj?.ticketId
          ? {
              workId: workItemObj?.workId || workItemObj?.ticketId,
              module: workItemObj?.module || 'TICKET'
            }
          : null
    };
    if (taskId) {
      inputObj.task = {
        taskId: taskId
      };
    }
    if (selectedItem) {
      inputObj = {
        ...inputObj,
        [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
          itemId: selectedItem?.itemId
        }
      };
    }

    if (createWorklogTimerEntryLoading || updateWorklogTimerEntryLoading)
      return;
    if (!timerId) {
      createWorklogTimerEntry({
        variables: {
          timerEntryInput: inputObj
        }
      }).then((res) => {
        if (res?.data?.createWorklogTimerEntry) {
          handleNewTicketCreation(
            res?.data?.createWorklogTimerEntry,
            workItemObj
          );
        }
      });
    } else {
      // if (TIMER_ID) {
      //   clearTimeout(TIMER_ID);
      // }
      updateWorklogTimerEntry({
        variables: {
          timerEntryInput: {
            ...inputObj,
            timerId
          }
        }
      }).then((res) => {
        if (res?.data?.updateWorklogTimerEntry) {
          handleNewTicketCreation(
            res?.data?.updateWorklogTimerEntry,
            workItemObj
          );
        }
      });
    }
  };

  // const associateWorkItemHandler = (newWorkItem) => {
  //   updateWorklogTimerEntry({
  //     variables: {
  //       timerEntryInput: {
  //         timerId,
  //         workItem: newWorkItem,
  //         notes: comments
  //       }
  //     }
  //   }).then((res) => {
  //     if (res) {
  //       toast('success', 'WorkItem associated successfully.');
  //       if (handleAssociateWorkItem) {
  //         handleAssociateWorkItem(res?.data?.updateWorklogTimerEntry?.workItem);
  //       }
  //     }
  //   });
  // };

  // useEffect(() => {
  //   EventEmitter.subscribe(
  //     'createWorkLogFromCreateTicket',
  //     handleCreateWorkLogFromCreateTicket
  //   );
  //   return function cleanup() {
  //     EventEmitter.unsubscribe('createWorkLogFromCreateTicket');
  //   };
  // });

  useImperativeHandle(forwardedRef, () => {
    let inputObj = {
      billable,
      notes: comments,
      type: 'AUTOMATIC',
      timerId: timerId,
      running: timer?.running
    };
    if (selectedItem) {
      inputObj = {
        ...inputObj,
        [contractsNew ? 'serviceCatalogItem' : 'serviceItem']: {
          itemId: selectedItem?.itemId
        }
      };
    }
    return {
      getTimerInputObj: inputObj
    };
  });

  const isLoading =
    createWorklogTimerEntryLoading ||
    updateWorklogTimerEntryLoading ||
    createWorklogEntryFromTimerLoading ||
    deleteWorklogTimerEntryLoading;

  const timerDataChanged = !isEqual(
    pick(timer, [
      'notes',
      'billable',
      'workItem',
      'serviceItem',
      'serviceCatalogItem'
    ]),
    {
      notes: comments,
      billable: billable,
      workItem: workItem,
      serviceItem: selectedItem,
      serviceCatalogItem: selectedItem
    }
  );

  return (
    <Wrapper
      className={`p20 full-width ${
        isLoading ? 'disabled pointer-events-none' : 'pointer-events-all'
      }`}
    >
      <span className="fx actr g10 timer-actions position-absolute">
        {!hideDelete && timerId && (
          <DeleteModal
            modalActivator={
              <span className="delete-timer ptr">
                <Tooltip
                  title={getTranslatedText(
                    intl,
                    'admin.timeTracking.timer.delete.started'
                  )}
                  position="right"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <SVGIcon name="Delete" size="sm" />
                </Tooltip>
              </span>
            }
            title={getTranslatedText(intl, 'timer.unknown.delete.title')}
            handleDeleteConfirmation={resetWorklog}
            description={getTranslatedText(
              intl,
              'timer.unknown.delete.description'
            )}
          />
        )}
        {!hideCancel && (
          <span className="close-popup ptr" onClick={handleClose}>
            <SVGIcon name="Close" size="sm" />
          </span>
        )}
      </span>
      <div
        className="full-width grid g10"
        style={{ gridTemplateColumns: '100%' }}
      >
        {hideWorkitem && !hideWorklogTask && (
          <WorklogTask
            task={associatedTaskToTimer}
            workItem={workItem}
            hideTaskWorkItem={hideTaskWorkItem}
            viewOnly={taskViewOnly}
          />
        )}

        <AccessControl
          productToCheck="MSP"
          render={
            <>
              {(workItem?.client?.accountId || workItem?.client?.name) && (
                <div className="flex center full-width">
                  <SVGIcon name="Cart" />
                  <DataGetter
                    className={`${
                      running ? 'timer-bg__highlight' : 'timer-bg'
                    } full-width ml-10 pl10 br-5`}
                  >
                    <Dropdown
                      className={``}
                      classNamePrefix="value_selector"
                      formatGroupLabel={formatGroupLabel}
                      components={{ IndicatorSeparator, DropdownIndicator }}
                      title={getTranslatedText(
                        intl,
                        'admin.timeTracking.chooseDefaultTimerService'
                      )}
                      onChange={(val) => {
                        const selectedValue = {
                          ...pick(val, ['itemId', 'name'])
                        };
                        handlePropertyChange(
                          contractsNew ? 'serviceCatalogItem' : 'serviceItem',
                          selectedValue
                        );
                      }}
                      options={options}
                      placeholder={getTranslatedText(
                        intl,
                        'admin.timeTracking.chooseDefaultTimerService.placeholder'
                      )}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      closeMenuOnScroll={(e) => {
                        return (
                          e.target &&
                          e.target.classList &&
                          !(e.target.classList.value.indexOf('menu-list') > -1)
                        );
                      }}
                      value={
                        selectedItem
                          ? {
                              ...selectedItem,
                              label: selectedItem.name,
                              value: selectedItem.name
                            }
                          : null
                      }
                      name="valueselector"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                      noOptionsMessage={() => {
                        return itemsForTimeTrackingLoading
                          ? 'Loading...'
                          : 'No options';
                      }}
                      isClearable={false}
                    />
                  </DataGetter>
                </div>
              )}
              {flags?.blockItemConsumptionInfo &&
                !hideBlockItemConsumptionInfo && (
                  <BlockItemConsumptionInfo
                    selectedItem={selectedItem}
                    ticket={workItem}
                    project={workItem}
                  />
                )}
            </>
          }
        />
        <div className="flex center full-width">
          <span className="self-start mt-8">
            <Avatar initials={`${currentUser.name[0]}`} size="extraSmall" />
          </span>
          <div className="w100">
            <DataGetter
              className={`${
                running ? 'timer-bg__highlight' : 'timer-bg'
              } full-width ml-10 pl10 br-5`}
            >
              <TextField
                placeholder={getTranslatedText(
                  intl,
                  'workItem.details.add.note'
                )}
                multiline
                value={comments}
                onChange={(val) => {
                  handlePropChange('comments', val);
                }}
                maxLength={TEXT_LENGTH.size_9}
                // disabled={!workItem && !selectedItem}
                error={errors?.notes?.[0]}
                onBlur={(event) => {
                  if (
                    preventBlurRef.current &&
                    preventBlurRef.current.contains(event.relatedTarget)
                  ) {
                    return;
                  }
                  // if (workItem?.workId) {
                  if (TIMER_ID) {
                    clearTimeout(TIMER_ID);
                  }
                  TIMER_ID = setTimeout(() => {
                    handlePropertyChange('notes', comments);
                  }, 500);
                  // }
                }}
                autoFocus={autoFocusTimerNotes}
              />
            </DataGetter>
            {aiGeneratedNote && (
              <>
                <div className="flex g5 actr mt-10 ml-10">
                  <div className="flex ignoreOverride">
                    <SVGIcon name="AIWand" />
                  </div>

                  <div>
                    <TextStyle>
                      <FormattedMessage id="ai.generated.worklog.note" />
                    </TextStyle>
                  </div>

                  <div ref={preventBlurRef}>
                    <Button
                      plain
                      onClick={() => {
                        if (setAIgeneratedNote) {
                          setAIgeneratedNote(false);
                        }
                        handlePropChange('comments', timerNote || '');
                      }}
                    >
                      <FormattedMessage id="ai.worklog.reset.selected.text" />
                    </Button>
                  </div>
                </div>
                <div className="p5 mb-14">
                  <AIOnboardingRatingComponent
                    nocomments
                    variant="transparent"
                    featureName={'WORKLOG_NOTE_COMPOSE'}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {isAgentChat && showAiNote && (
          <div className="flex actr g10 full-width">
            <SVGIcon name="AiSparkleOutline" color="#9A7BFF" />
            <TextStyle>
              <FormattedMessage id="ai.generated.worklog.note" />
            </TextStyle>
            <TextStyle>
              <Link
                onClick={() => {
                  handlePropertyChange('comments', '');
                  setShowAiNote(false);
                }}
              >
                <FormattedMessage id="common.clear" />
              </Link>
            </TextStyle>
          </div>
        )}
        {!hideWorkitem ? (
          associatedWorkItem || (!associatedWorkItem && associateWorkItem) ? (
            <>
              <AssociateWorkItem
                timer={timer}
                highlight={running}
                associatedWorkItem={associatedWorkItem}
                reAssociateWorkItem={
                  associatedTaskToTimer ? false : reAssociateWorkItem
                }
                handleParentClose={handleParentClose}
                handleAssociateWorkItem={(module, workitem) => {
                  if (!timerId) {
                    handleAssociateWorkItem(module, workitem);
                  } else {
                    handlePropertyChange(
                      'workItem',
                      pick(workitem, ['workId', 'module'])
                    );
                  }
                }}
              />

              {!hideWorklogTask && (
                <div className="ml-30">
                  <WorklogTask
                    task={associatedTaskToTimer}
                    workItem={workItem}
                    hideTaskWorkItem={hideTaskWorkItem}
                    viewOnly={taskViewOnly}
                  />
                </div>
              )}
            </>
          ) : null
        ) : null}
        <div className="flex center full-width">
          <Timer className="flex full-width center">
            <div
              className="flex center pointer"
              onClick={() => {
                // if (editMode) {
                //   handlePropChange('running', !isRunning);
                //   isRunning ? pause() : start();
                // } else {
                handleTimerClick(!running);
                // }
              }}
            >
              <SVGIcon name={running ? 'Pause' : 'Play'} />
              <TimerCell
                className={`${
                  running ? 'timer-bg__highlight' : 'timer-bg'
                } ml-10 flex center br-5 px-10p`}
                highlight={running}
              >
                <TextStyle weight="SemiBold">
                  {/* {days ? <span>{formatTime(days)}:</span> : ''} */}
                  <span>{formatTime(days ? days * 24 + hours : hours)}</span>:
                  <span>{formatTime(minutes)}</span>:
                  <span>{formatTime(seconds)}</span>
                </TextStyle>
              </TimerCell>
            </div>
            <AccessControl
              productToCheck={COMMON_CONSTANTS.MSP}
              render={
                <Tooltip
                  title={getTranslatedText(
                    intl,
                    billable
                      ? 'worklog.details.markedAsBillable'
                      : 'worklog.details.markAsBillable'
                  )}
                  position="top-start"
                  inertia
                  className="mlauto"
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <IconHolder
                    selected={billable}
                    className="mlauto border-black br-5"
                    onClick={handleBillable}
                    padding="3px"
                    size="24px"
                    iconsize="16px"
                  >
                    <SVGIcon name="Billable" />
                  </IconHolder>
                </Tooltip>
              }
            />
          </Timer>
          {/* <DisabledComponent
            canRender={!!selectedItem}
            tooltipText={getTranslatedText(
              intl,
              'admin.timeTracking.chooseDefaultTimerService.placeholder'
            )}
            render={}
          /> */}
        </div>
        {timerId ? (
          <ButtonGroup>
            {workItem?.workId ? (
              <Button
                loading={createWorklogEntryFromTimerLoading}
                onClick={handleAddWorklog}
                plain
              >
                <FormattedMessage id="workItem.details.worklog.timer.addAsWorklog" />
              </Button>
            ) : null}
            {/* {editMode && timerDataChanged && (
              <Button
                loading={
                  createWorklogTimerEntryLoading ||
                  updateWorklogTimerEntryLoading ||
                  deleteWorklogTimerEntryLoading
                }
                onClick={handleTimerClick}
                plain
              >
                <FormattedMessage
                  id={
                    timer?.timerId
                      ? 'ticket.details.worklog.manual.update'
                      : 'ticket.details.worklog.manual.save'
                  }
                />
              </Button>
            )} */}
            {/* {!timerId && (
              <Button plainSecondary onClick={resetWorklog}>
                <FormattedMessage id="ticket.details.worklog.timer.cancel" />
              </Button>
            )} */}
          </ButtonGroup>
        ) : (
          ''
        )}
      </div>
    </Wrapper>
  );
}

export default injectIntl(WorklogAutoTimerForm);
